// Command Handler - processes chat commands
import { useCritterStore, CRITTER_TYPES } from '../store/critterStore'
import useSettingsStore from '../store/settingsStore'
import { twitchApi } from './twitchApi'

// Helper to get current settings
const getSettings = () => useSettingsStore.getState().settings

class CommandHandler {
  constructor() {
    this.commands = new Map()
    this.registerCommands()
  }

  // Register all available commands
  registerCommands() {
    // !joincritter [type] - Join and create a critter
    this.commands.set('joincritter', {
      requiresTier3: true,
      handler: async (userId, username, args, store) => {
        if (store.hasCritter(userId, username)) {
          console.log(`${username} already has a critter`)
          return
        }

        let critterType = 'ferret'
        if (args.length > 0) {
          const requestedType = args[0].toLowerCase()
          if (CRITTER_TYPES[requestedType]) {
            critterType = requestedType
          }
        }

        store.addCritter(userId, username, critterType)
      }
    })

    // !feed - Feed your critter
    this.commands.set('feed', {
      requiresTier3: true,
      handler: async (userId, username, args, store) => {
        console.log(`!feed command: userId=${userId}, username=${username}`)
        if (store.hasCritter(userId, username)) {
          store.feedCritter(userId, username)
        } else {
          console.log(`${username} doesn't have a critter yet`)
        }
      }
    })

    // !pet - Pet your critter
    this.commands.set('pet', {
      requiresTier3: true,
      handler: async (userId, username, args, store) => {
        console.log(`!pet command: userId=${userId}, username=${username}`)
        if (store.hasCritter(userId, username)) {
          store.petCritter(userId, username)
        } else {
          console.log(`${username} doesn't have a critter yet`)
        }
      }
    })

    // !dance - Make your critter dance
    this.commands.set('dance', {
      requiresTier3: true,
      handler: async (userId, username, args, store) => {
        if (store.hasCritter(userId, username)) {
          store.danceCritter(userId, username)
        } else {
          console.log(`${username} doesn't have a critter yet`)
        }
      }
    })

    // !trick - Make your critter do a trick
    this.commands.set('trick', {
      requiresTier3: true,
      handler: async (userId, username, args, store) => {
        if (store.hasCritter(userId, username)) {
          store.trickCritter(userId, username)
        } else {
          console.log(`${username} doesn't have a critter yet`)
        }
      }
    })

    // !hug @username - Hug another viewer's critter
    this.commands.set('hug', {
      requiresTier3: true,
      handler: async (userId, username, args, store) => {
        if (!store.hasCritter(userId, username)) {
          console.log(`${username} doesn't have a critter yet`)
          return
        }
        if (args.length === 0) {
          console.log('Usage: !hug @username')
          return
        }
        const targetUsername = args[0].toLowerCase().replace('@', '')
        if (targetUsername === username.toLowerCase()) return // Can't hug yourself
        if (!store.hasCritter(null, targetUsername)) {
          console.log(`${targetUsername} doesn't have a critter`)
          return
        }
        store.hugCritter(userId, username, targetUsername)
      }
    })

    // !bonk @username - Bonk another viewer's critter
    this.commands.set('bonk', {
      requiresTier3: true,
      handler: async (userId, username, args, store) => {
        if (!store.hasCritter(userId, username)) {
          console.log(`${username} doesn't have a critter yet`)
          return
        }
        if (args.length === 0) {
          console.log('Usage: !bonk @username')
          return
        }
        const targetUsername = args[0].toLowerCase().replace('@', '')
        if (targetUsername === username.toLowerCase()) return // Can't bonk yourself
        if (!store.hasCritter(null, targetUsername)) {
          console.log(`${targetUsername} doesn't have a critter`)
          return
        }
        store.bonkCritter(userId, username, targetUsername)
      }
    })

    // !crittertype [type] - Change critter type
    this.commands.set('crittertype', {
      requiresTier3: true,
      handler: async (userId, username, args, store) => {
        if (!store.hasCritter(userId, username)) {
          console.log(`${username} doesn't have a critter yet`)
          return
        }

        if (args.length === 0) {
          console.log('No critter type specified')
          return
        }

        const newType = args[0].toLowerCase()
        if (CRITTER_TYPES[newType]) {
          store.setCritterType(userId, newType)
          console.log(`${username} changed critter to ${newType}`)
        } else {
          console.log(`Invalid critter type: ${newType}`)
        }
      }
    })

    // !leavecritter - Remove your critter
    this.commands.set('leavecritter', {
      requiresTier3: true,
      handler: async (userId, username, args, store) => {
        if (store.removeCritter(userId)) {
          console.log(`${username}'s critter has left`)
        }
      }
    })

    // !removecritter {username} - Admin command to remove a user's critter
    this.commands.set('removecritter', {
      requiresMod: true,
      handler: async (userId, username, args, store) => {
        if (args.length === 0) {
          console.log('Usage: !removecritter {username}')
          return
        }

        const targetUsername = args[0].toLowerCase().replace('@', '')
        const critters = useCritterStore.getState().critters
        let found = false

        Object.values(critters).forEach((critter) => {
          if (critter.username.toLowerCase() === targetUsername) {
            store.removeCritter(critter.userId)
            console.log(`Admin ${username} removed ${targetUsername}'s critter`)
            found = true
          }
        })

        if (!found) {
          console.log(`No critter found for ${targetUsername}`)
        }
      }
    })

    // !addcritter {username} [type] - Admin command to add a critter for someone
    this.commands.set('addcritter', {
      requiresMod: true,
      handler: async (userId, username, args, store) => {
        if (args.length === 0) {
          console.log('Usage: !addcritter {username} [type]')
          return
        }

        // Normalize username to lowercase to match Twitch's format
        const targetUsername = args[0].replace('@', '').toLowerCase()
        const critterType = args[1]?.toLowerCase() || 'ferret'

        if (!CRITTER_TYPES[critterType]) {
          console.log(`Invalid critter type: ${critterType}. Available: ${Object.keys(CRITTER_TYPES).join(', ')}`)
          return
        }

        const pseudoId = `manual_${targetUsername}_${Date.now()}`
        store.addCritter(pseudoId, targetUsername, critterType)
        console.log(`Admin ${username} added ${critterType} critter for ${targetUsername}`)
      }
    })

    // !feast - Admin command to feed all critters at once
    this.commands.set('feast', {
      requiresMod: true,
      handler: async (userId, username, args, store) => {
        store.rainFood()
        console.log(`Admin ${username} made it feast food on all critters!`)
      }
    })

    // !clearcritters - Admin command to remove all critters
    this.commands.set('clearcritters', {
      requiresMod: true,
      handler: async (userId, username, args, store) => {
        store.removeAll()
        console.log(`Admin ${username} cleared all critters`)
      }
    })

    // !admincritter [subcommand] - Admin commands (legacy)
    this.commands.set('admincritter', {
      requiresMod: true,
      handler: async (userId, username, args, store) => {
        if (args.length === 0) return

        const subCommand = args[0].toLowerCase()

        switch (subCommand) {
          case 'clear':
            store.removeAll()
            console.log('Admin cleared all critters')
            break

          case 'remove':
            if (args.length > 1) {
              const targetUsername = args[1].toLowerCase().replace('@', '')
              const critters = useCritterStore.getState().critters
              Object.values(critters).forEach((critter) => {
                if (critter.username.toLowerCase() === targetUsername) {
                  store.removeCritter(critter.userId)
                  console.log(`Admin removed ${targetUsername}'s critter`)
                }
              })
            }
            break

          case 'add':
            if (args.length > 1) {
              // Normalize username to lowercase to match Twitch's format
              const targetUsername = args[1].toLowerCase()
              const critterType = args[2]?.toLowerCase() || 'ferret'
              const pseudoId = `manual_${Date.now()}`
              store.addCritter(pseudoId, targetUsername, critterType)
              console.log(`Admin added critter for ${targetUsername}`)
            }
            break

          default:
            console.log(`Unknown admin command: ${subCommand}`)
        }
      }
    })
  }

  // Process a chat message
  async processMessage(chatMessage) {
    const { username, userId, message } = chatMessage
    const store = useCritterStore.getState()
    const settings = getSettings()

    // Update activity timestamp and wake critter if sleeping
    if (store.hasCritter(userId, username)) {
      store.touchCritter(userId, username)
    }

    // Check if message starts with !
    if (!message.startsWith('!')) {
      // Not a command - show chat bubble if user has critter
      if (store.hasCritter(userId, username)) {
        store.showChatBubble(userId, message, username)
      }
      return
    }

    // Parse command and arguments
    const parts = message.slice(1).split(' ')
    const commandName = parts[0].toLowerCase()
    const args = parts.slice(1)

    // Get command config
    const command = this.commands.get(commandName)
    if (!command) {
      return // Unknown command, ignore
    }

    // Check feature toggles
    if (commandName === 'feed' && !settings.allowFeeding) return
    if (commandName === 'pet' && !settings.allowPetting) return
    if (commandName === 'dance' && !settings.allowDancing) return
    if (commandName === 'trick' && !settings.allowTricks) return
    if (commandName === 'hug' && !settings.allowHugging) return
    if (commandName === 'bonk' && !settings.allowBonking) return
    if (commandName === 'crittertype' && !settings.allowCritterTypeChange) return

    // Check permissions
    if (command.requiresMod) {
      // Check if broadcaster-only admin is enabled
      if (settings.broadcasterOnlyAdmin) {
        const isBroadcaster = await twitchApi.checkIsBroadcaster(userId)
        if (!isBroadcaster) {
          console.log(`${username} tried to use admin command (broadcaster-only mode)`)
          return
        }
      } else if (settings.modCanUseAdminCommands) {
        const isMod = await twitchApi.checkModerator(userId)
        if (!isMod) {
          console.log(`${username} tried to use mod command without permission`)
          return
        }
      } else {
        // Only broadcaster can use admin commands
        const isBroadcaster = await twitchApi.checkIsBroadcaster(userId)
        if (!isBroadcaster) {
          console.log(`${username} tried to use admin command without permission`)
          return
        }
      }
    }

    if (command.requiresTier3) {
      // Check if subscription is required
      if (settings.requireSubscription) {
        const subscription = await twitchApi.checkSubscription(userId)
        if (!subscription || parseInt(subscription.tier) < settings.requiredTier) {
          const tierName = settings.requiredTier === 1000 ? 'Tier 1' : settings.requiredTier === 2000 ? 'Tier 2' : 'Tier 3'
          console.log(`${username} is not a ${tierName} subscriber`)
          return
        }
      }
      // If subscription not required, allow anyone
    }

    // Execute command
    try {
      await command.handler(userId, username, args, store)
    } catch (error) {
      console.error(`Error executing command ${commandName}:`, error)
    }
  }
}

// Singleton instance
export const commandHandler = new CommandHandler()